import type { TableLabels } from "./types";

/**
 * English default strings. Consumers override any subset via the
 * `labels` option; {@link resolveLabels} merges their overrides on top.
 */
export const defaultLabels: Required<TableLabels> = {
  table: "Data table",
  search: "Search",
  searchPlaceholder: "Search…",
  noData: "No data",
  noResults: "No results match your filters",
  pageSelected: (count) => `All ${count} on this page selected`,
  selectAllMatching: (total) => `Select all ${total} matching`,
  allMatchingSelected: (total) => `All ${total} matching selected`,
  expandRow: "Expand row",
  collapseRow: "Collapse row",
  operator: "Operator",
  value: "Value",
  from: "From",
  to: "To",
  opEqual: "Equal",
  opAtLeast: "At least",
  opAtMost: "At most",
  opBetween: "Between",
  opOn: "On",
  opOnOrAfter: "On or after",
  opOnOrBefore: "On or before",
  opNotEqual: "Not equal",
  opGreater: "Greater than",
  opLess: "Less than",
  opContains: "Contains",
  opNotContains: "Does not contain",
  opStartsWith: "Starts with",
  opEndsWith: "Ends with",
  opEmpty: "Is empty",
  opNotEmpty: "Is not empty",
  opIn: "Is any of",
  opNotIn: "Is none of",
  opBefore: "Before",
  opAfter: "After",
  opRelative: "Relative",
  relToday: "Today",
  relYesterday: "Yesterday",
  relTomorrow: "Tomorrow",
  relThisWeek: "This week",
  relThisMonth: "This month",
  relPreviousMonth: "Previous month",
  relLastN: "Last N days",
  relNextN: "Next N days",
  boolAny: "Any",
  boolTrue: "True",
  boolFalse: "False",
  savedViews: "Saved views",
  saveView: "Save view",
  viewName: "View name",
  deleteView: "Delete view",
  renameView: "Rename view",
  applyView: "Apply view",
  moveViewUp: "Move view up",
  moveViewDown: "Move view down",
  setDefaultView: "Set as default",
  defaultViewBadge: "Default",
  readOnlyViewBadge: "Read-only",
  loading: "Loading…",
  loadMore: "Load more",
  filters: "Filters",
  clearAll: "Clear all",
  removeFilter: (label) => `Remove filter: ${label}`,
  filtersDone: "Done",
  filterTree: "Advanced",
  filterAddCondition: "Add condition",
  filterAddGroup: "Add group",
  filterCombinatorAnd: "AND",
  filterCombinatorOr: "OR",
  filterRemoveCondition: "Remove condition",
  filterRemoveGroup: "Remove group",
  filterField: "Field",
  checklistSearch: "Search values",
  checklistClear: "Clear",
  checklistNoValues: "No matching values",
  headerFilters: "Column filters",
  sortBy: "Sort by",
  rowsPerPage: "Rows per page",
  actions: "Actions",
  selectAll: "Select all",
  selectRow: "Select row",
  selectColumn: "Select column",
  cancel: "Cancel",
  retry: "Retry",
  errorTitle: "Something went wrong",
  errorMessage: "We couldn't load this data.",
  previousPage: "Previous page",
  nextPage: "Next page",
  goToPage: (page) => `Go to page ${page}`,
  selectedCount: (count) => `${count} selected`,
  showing: ({ from, to, total }) => `Showing ${from}–${to} of ${total}`,
  pageOf: ({ page, total }) => `Page ${page} of ${total}`,
  columns: "Columns",
  pinStart: "Pin to start",
  pinEnd: "Pin to end",
  unpin: "Unpin",
  moveStart: "Move to start",
  moveEnd: "Move to end",
  resetColumns: "Reset columns",
  autoSizeColumns: "Size columns to content",
  autoSizeColumn: "Size column to content",
  resizeColumn: "Resize column",
  showColumn: "Show column",
  hideColumn: "Hide column",
  searchColumns: "Search columns",
  showAllColumns: "Show all",
  hideAllColumns: "Hide all",
  unpinAllColumns: "Unpin all",
  resetColumn: "Reset column",
  sortAscending: "Sort ascending",
  sortDescending: "Sort descending",
  filterColumn: "Filter column",
  columnActions: "Column actions",
  exportCsv: "Export CSV",
  exportFile: (format) => `Export ${format.toUpperCase()}`,
  exportDone: "Export complete",
  exportFailed: "Export failed",
  editCell: "Edit cell",
  undoEdit: "Undo",
  redoEdit: "Redo",
  editRow: "Edit row",
  saveRow: "Save row",
  pendingRows: (count) =>
    count === 1 ? "1 unsaved row" : `${String(count)} unsaved rows`,
  saveAll: "Save all",
  cancelAll: "Cancel all",
  addRow: "Add row",
  duplicateRow: "Duplicate row",
  deleteRow: "Delete row",
  deleteRowConfirm: "Delete this row? This cannot be undone.",
  rowActionsMenu: "Row actions",
  editConflict: "This row changed while you were editing",
  keepMine: "Keep mine",
  takeTheirs: "Take theirs",
  theirsValue: (value) => `Theirs: ${value}`,
  reorderRow: "Reorder row",
  moveRowUp: "Move row up",
  moveRowDown: "Move row down",
  rowLifted: (position) => `Row ${String(position)} lifted`,
  rowMoved: (from, to) => `Row moved from ${String(from)} to ${String(to)}`,
  rowReorderCancelled: "Reorder cancelled",
  pinToTop: "Pin to top",
  pinToBottom: "Pin to bottom",
  unpinRow: "Unpin row",
  rowSeparator: "Separator",
  expandColumnGroup: "Expand column group",
  collapseColumnGroup: "Collapse column group",
  gridRangeCopied: (cells) =>
    `${cells} ${cells === 1 ? "cell" : "cells"} copied`,
  gridRangeCopyFailed: "Copy failed",
  gridRangePasted: (cells) =>
    `${cells} ${cells === 1 ? "cell" : "cells"} pasted`,
  gridRangePasteFailed: "Paste failed",
  gridRangeFilled: (cells) =>
    `${cells} ${cells === 1 ? "cell" : "cells"} filled`,
  gridFillHandle: "Fill from selection",
  selectionCount: "Count",
  selectionSum: "Sum",
  selectionAverage: "Avg",
  selectionMin: "Min",
  selectionMax: "Max",
  editUndone: (cells) => `${cells} ${cells === 1 ? "cell" : "cells"} restored`,
  editRedone: (cells) => `${cells} ${cells === 1 ? "cell" : "cells"} redone`,
  editNothingToUndo: "Nothing to undo",
  findInTable: "Find in table",
  findPlaceholder: "Find in table",
  findMatchCount: (current, total) =>
    total === 0 ? "No matches" : `${current} of ${total}`,
  findPrevious: "Previous match",
  findNext: "Next match",
  findClose: "Close find",
  sidePanel: "Table settings",
  contextMenu: "Table actions",
  commandPalette: "Command palette",
  commandSearch: "Search commands",
  commandEmpty: "No matching command",
  print: "Print",
  density: "Density",
  densityComfortable: "Comfortable",
  densityCompact: "Compact",
  enterFullscreen: "Enter fullscreen",
  exitFullscreen: "Exit fullscreen",
  copyCells: "Copy",
  cutCells: "Cut",
  closePanel: "Close panel",
  pivotRows: "Rows",
  pivotColumns: "Columns",
  pivotMeasures: "Measures",
  pivotAdd: "Add field",
  pivotRemove: "Remove field",
  pivotMoveUp: "Move up",
  pivotMoveDown: "Move down",
  pivotAggregation: "Aggregation",
  pivotTotal: "Total",
  pivotGrandTotal: "Grand total",
  gridCellPosition: (row, total) => `row ${row} of ${total}`,
  gridRangeSelection: ({ fromRow, toRow, fromColumn, toColumn, cells }) =>
    `selected rows ${fromRow} to ${toRow}, columns ${fromColumn} to ${toColumn}, ${cells} cells`,
  moreGroups: (remaining) => `Show ${remaining} more groups`,
  moreRowsInGroup: (remaining) => `Show ${remaining} more in this group`,
  groupTotal: (label) => `${label} total`,
  expandGroup: "Expand group",
  collapseGroup: "Collapse group",
  groupCount: (count) => `(${count})`,
  noticeVirtualizePaged:
    "Virtualization is off — this paged table shows one page at a time.",
  noticePinNested: "Row pinning is off while grouping or a tree is on.",
  noticeReorderNested: "Row reorder is off while grouping or a tree is on.",
  noticeGroupingUnavailable:
    "Grouping is off — this source does not provide the full filtered set.",
  noticeExportAllPage:
    "Export all is this page — the full filtered set is not available.",
  noticeEditWithoutWriter: "Editing is off — no write handler is wired.",
  exportThisPage: "Export this page",
};

/**
 * Merge caller overrides over {@link defaultLabels}. Undefined entries in
 * the override are ignored, so partial `labels` objects are safe.
 *
 * @param overrides - A partial set of label overrides.
 * @returns A fully-populated, immutable label set.
 */
export function resolveLabels(
  overrides: TableLabels | undefined
): Required<TableLabels> {
  if (!overrides) return defaultLabels;
  const merged = { ...defaultLabels };
  for (const key of Object.keys(overrides) as (keyof TableLabels)[]) {
    const value = overrides[key];
    if (value !== undefined) {
      // Each key's value type matches the same key in the target.
      (merged[key] as unknown) = value;
    }
  }
  return merged;
}
